import { useState, useEffect } from 'react';

import actions from '../constants/actions';

import useEntityData from './useEntityData';
import { BoardMembers, Signatories } from '../components/business/editTabs/kyc/CompanyRepresentation';
import { AltBeneficialOwners, BeneficialOwners } from '../components/business/editTabs/kyc/OwnerShip';
import isDefinedFilter from '../utilities/isDefinedFilter';
import {
  boardMembersToRelatedIndividuals,
  currentBoardMembersToRelatedIndividuals,
  mergeByEntityId,
  RelatedIndividual,
} from '../utilities/relatedIndividuals';

const useRelatedEntities = () => {
  const [entities, setEntities] = useState<RelatedIndividual[]>([]);
  const [currentEntities, setCurrentEntities] = useState<RelatedIndividual[]>([]);
  const {
    data: boardMembersData,
    isLoading: boardMembersIsLoading,
  } = useEntityData<BoardMembers>({
    resource: 'businesses',
    source: 'board-members',
    getAction: actions.BUSINESS_GET_COMPANY_REP,
  });
  const {
    data: beneficialOwnersData,
    isLoading: benOwnerIsLoading,
  } = useEntityData<BeneficialOwners>({
    resource: 'businesses',
    source: 'beneficial-owners',
    getAction: actions.BUSINESS_GET_OWNERSHIP,
  });
  const {
    data: altBeneficialOwnersData,
    isLoading: altBenOwnerIsLoading,
  } = useEntityData<AltBeneficialOwners>({
    resource: 'businesses',
    source: 'alt-beneficial-owners',
    getAction: actions.BUSINESS_GET_OWNERSHIP,
  });
  const {
    data: signatoryData,
    isLoading: signatoryIsLoading,
  } = useEntityData<Signatories>({
    resource: 'businesses',
    source: 'signatories',
    getAction: actions.BUSINESS_GET_COMPANY_REP,
  });

  const updateDate = boardMembersData?.createdAt
    || beneficialOwnersData?.createdAt
    || altBeneficialOwnersData?.createdAt
    || signatoryData?.createdAt;

  useEffect(() => {
    const otherRelatedIndividuals: RelatedIndividual[] = [
      ...(beneficialOwnersData?.beneficialOwners?.map((owner) => ((
        owner.entityId && owner.entityType
      ) ? {
          name: [owner.person?.firstNames?.join(' '), owner.person?.surName].filter((name) => !!name).join(' '),
          entityId: owner.entityId,
          entityType: owner.entityType,
          relation: 'Beneficial owner',
        } : undefined)).filter(isDefinedFilter) ?? []),
      ...(altBeneficialOwnersData?.altBeneficialOwners?.map((owner) => ((
        owner.entityId && owner.entityType
      ) ? {
          name: owner.name,
          entityId: owner.entityId,
          entityType: owner.entityType,
          relation: 'Alternative beneficial owner',
        } : undefined)).filter(isDefinedFilter) ?? []),
      ...(signatoryData?.records?.at(0)?.signatories.map((signatory) => ((
        signatory.person?.entityId && signatory.person?.entityType
      ) ? {
          name: [signatory.person.firstName, signatory.person.lastName].filter((name) => !!name).join(' '),
          entityId: signatory.person.entityId,
          entityType: signatory.person.entityType,
          relation: 'Signatory',
        } : undefined)).filter(isDefinedFilter) ?? []),
    ];

    setEntities(mergeByEntityId([
      ...boardMembersToRelatedIndividuals(boardMembersData?.boardMembers),
      ...otherRelatedIndividuals,
    ]));
    setCurrentEntities(mergeByEntityId([
      ...currentBoardMembersToRelatedIndividuals(boardMembersData?.boardMembers),
      ...otherRelatedIndividuals,
    ]));
  }, [boardMembersData, beneficialOwnersData, altBeneficialOwnersData, signatoryData]);

  return {
    boardMembersData,
    entities,
    currentEntities,
    beneficialOwners: beneficialOwnersData?.beneficialOwners,
    altBeneficialOwners: altBeneficialOwnersData?.altBeneficialOwners,
    isLoading: boardMembersIsLoading || benOwnerIsLoading || altBenOwnerIsLoading
      || signatoryIsLoading,
    updateDate,
  };
};

export default useRelatedEntities;
